import {
  Briefcase,
  Building2,
  CalendarDays,
  Car,
  Clock,
  Hash,
  Layers,
  LayoutGrid,
  MonitorSmartphone,
  Tv,
  X,
} from 'lucide-react';
import { useMemo } from 'react';

import { CustomSelect } from '@/components/CustomSelect';
import { CONTENT_ALIGN_PX } from '@/components/layout/layoutConstants';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import type { BriefingRow } from '../briefing/api';
import {
  ASSET_TYPE_KEYS,
  ASSET_TYPES,
  assetTypeLabel,
  type AssetType,
} from '../../constants/assetTypes';
import {
  CHANNEL_OPTIONS,
  filmDurationLabel,
  tierFilterLabel,
  USAGE_RIGHTS_STATUS_OPTIONS,
} from '../../constants/taxonomy';
import { useVocabulary, vocabularyLabel } from '../../vocabulary/useVocabulary';
import type { AssetFilterState } from './useAssetFilters';

/** "Source image, Master image, Filmic or OLA banner" — built from the registry so it can't drift. */
function assetTypeTooltip(keys: readonly AssetType[]): string {
  const labels = keys.map((key) => ASSET_TYPES[key].label);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
}

interface AssetFilterDrawerProps {
  isOpen: boolean;
  filters: AssetFilterState;
  onFilterChange: (filters: AssetFilterState) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
  briefingRows?: BriefingRow[];
  /**
   * The kinds the caller can see, and so may filter by. Narrower than the
   * registry for roles without `can_view_source_images`: offering a kind the
   * backend will never return is a filter that can only ever come back empty.
   */
  assetTypes?: readonly AssetType[];
}

export function AssetFilterDrawer({
  isOpen,
  filters,
  onFilterChange,
  onClearFilters,
  activeFilterCount,
  briefingRows = [],
  assetTypes = ASSET_TYPE_KEYS,
}: AssetFilterDrawerProps) {
  const { vocabulary } = useVocabulary();
  const campaignOptions = useMemo(() => {
    // Unique list of campaign IDs from briefing rows
    const seen = new Set<string>();
    const options: { id: string; label: string }[] = [];
    for (const row of briefingRows) {
      const key = row.id;
      if (!seen.has(key)) {
        seen.add(key);
        const nameplateSuffix = row.nameplate ? ` (${row.nameplate})` : '';
        options.push({
          id: key,
          label: `${row.campaign_id}${nameplateSuffix}`,
        });
      }
    }
    return options;
  }, [briefingRows]);

  const campaignLabelsMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of campaignOptions) {
      map.set(opt.id, opt.label);
    }
    return map;
  }, [campaignOptions]);

  return (
    <div
      className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out border-b border-slate-200 bg-white ${
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className={`min-h-0 min-w-0 ${isOpen ? 'overflow-visible' : 'overflow-hidden'}`}>
        <div
          className={`transition-[opacity,transform] duration-300 ease-in-out ${
            isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
        >
          {/* Main filter container */}
          <div
            className="w-full pt-3 pb-3 pr-4 flex flex-wrap items-center gap-2"
            style={{ paddingLeft: `${CONTENT_ALIGN_PX}px` }}
          >
            {/* 1. Nameplate */}
            <div className="w-[145px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Nameplate"
                      showLabel={false}
                      placeholder="Nameplate"
                      value={filters.nameplate}
                      options={vocabulary.nameplates.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, nameplate: v })}
                      multi
                      searchable
                      sortSelectedFirst
                      icon={<Car className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Ford approved vehicle nameplates</TooltipContent>
              </Tooltip>
            </div>

            {/* 1b. Carline code — independent of the nameplate */}
            <div className="w-[135px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Carline code"
                      showLabel={false}
                      placeholder="Carline"
                      value={filters.carline_code}
                      options={vocabulary.carline_codes.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, carline_code: v })}
                      multi
                      searchable
                      sortSelectedFirst
                      icon={<Hash className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Three-letter carline codes</TooltipContent>
              </Tooltip>
            </div>

            {/* 1c. Business unit */}
            <div className="w-[145px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Business unit"
                      showLabel={false}
                      placeholder="Business unit"
                      value={filters.business_unit}
                      options={vocabulary.business_units.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, business_unit: v })}
                      multi
                      searchable
                      renderOption={(v) => vocabularyLabel(vocabulary, v)}
                      icon={<Briefcase className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Ford Blue, Model E or Ford Pro</TooltipContent>
              </Tooltip>
            </div>

            {/* 2. Campaign */}
            {(campaignOptions.length > 0 || Boolean(filters.campaign)) && (
              <div className="w-[160px] shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <CustomSelect
                        label="Campaign"
                        showLabel={false}
                        placeholder="Campaign"
                        value={filters.campaign}
                        options={Array.from(
                          new Set([
                            ...campaignOptions.map((o) => o.id),
                            ...(filters.campaign
                              ? filters.campaign
                                  .split(',')
                                  .map((s) => s.trim())
                                  .filter(Boolean)
                              : []),
                          ]),
                        )}
                        onChange={(v) => onFilterChange({ ...filters, campaign: v })}
                        multi
                        searchable
                        renderOption={(id) => campaignLabelsMap.get(id) ?? id}
                        icon={<Layers className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Approved campaigns from Media Laydown
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            {/* 3. Tier */}
            <div className="w-[125px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Tier"
                      showLabel={false}
                      placeholder="Tier"
                      value={filters.tier}
                      options={vocabulary.tiers.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, tier: v })}
                      multi
                      searchable
                      renderOption={(v) =>
                        tierFilterLabel(v, vocabulary.tiers.find((o) => o.value === v)?.label ?? v)
                      }
                      icon={<Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Campaign tier / priority</TooltipContent>
              </Tooltip>
            </div>

            {/* 4. Channel */}
            <div className="w-[125px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Channel"
                      showLabel={false}
                      placeholder="Channel"
                      value={filters.channel}
                      options={CHANNEL_OPTIONS.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, channel: v })}
                      multi
                      searchable
                      renderOption={(v) => CHANNEL_OPTIONS.find((o) => o.value === v)?.label ?? v}
                      icon={<Tv className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Media channel (Social, Search, Programmatic)
                </TooltipContent>
              </Tooltip>
            </div>

            {/* 5. Kind of asset */}
            <div className="w-[130px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Type"
                      showLabel={false}
                      placeholder="Type"
                      value={filters.asset_type}
                      options={[...assetTypes]}
                      onChange={(v) => onFilterChange({ ...filters, asset_type: v })}
                      multi
                      searchable
                      renderOption={(v) => assetTypeLabel(v)}
                      icon={<LayoutGrid className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">{assetTypeTooltip(assetTypes)}</TooltipContent>
              </Tooltip>
            </div>

            {/* 6. Duration */}
            <div className="w-[145px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Duration"
                      showLabel={false}
                      placeholder="Duration"
                      value={filters.duration}
                      options={vocabulary.film_durations.map(String)}
                      onChange={(v) => onFilterChange({ ...filters, duration: v })}
                      multi
                      searchable
                      renderOption={(seconds) => filmDurationLabel(Number(seconds))}
                      icon={<Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Film duration (filters video assets)</TooltipContent>
              </Tooltip>
            </div>

            {/* 7. Aspect Ratio */}
            <div className="w-[140px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Aspect Ratio"
                      showLabel={false}
                      placeholder="Aspect Ratio"
                      value={filters.aspect_ratio}
                      options={vocabulary.film_format_sizes.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, aspect_ratio: v })}
                      multi
                      searchable
                      icon={<MonitorSmartphone className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">Film aspect ratio (filters video assets)</TooltipContent>
              </Tooltip>
            </div>

            {/* 8. Usage Rights Status */}
            <div className="w-[155px] shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <CustomSelect
                      label="Rights Status"
                      showLabel={false}
                      placeholder="Rights Status"
                      value={filters.usage_rights_status}
                      options={USAGE_RIGHTS_STATUS_OPTIONS.map((o) => o.value)}
                      onChange={(v) => onFilterChange({ ...filters, usage_rights_status: v })}
                      multi
                      searchable
                      renderOption={(v) =>
                        USAGE_RIGHTS_STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v
                      }
                      icon={<CalendarDays className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">License / Rights Expiration Status</TooltipContent>
              </Tooltip>
            </div>

            {/* 9. Reference ID (FCV ID) Search */}
            <div className="w-[155px] shrink-0 relative">
              <div className="relative">
                <input
                  type="text"
                  value={filters.reference_id}
                  onChange={(e) => onFilterChange({ ...filters, reference_id: e.target.value })}
                  placeholder="Reference / FCV ID"
                  aria-label="Reference ID"
                  className="w-full h-8 pl-8 pr-7 bg-white border border-slate-300 rounded-lg text-sm placeholder:text-slate-400 text-slate-800 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0014cc]/20 focus:border-[#0014cc] transition-colors"
                />
                <Hash className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                {filters.reference_id && (
                  <button
                    type="button"
                    onClick={() => onFilterChange({ ...filters, reference_id: '' })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                    aria-label="Clear reference ID"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Clear All Filters Button */}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={onClearFilters}
                className="h-8 px-2.5 rounded-lg text-xs font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition-colors inline-flex items-center gap-1 shrink-0"
              >
                <X className="w-3.5 h-3.5" />
                <span>Clear filters</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
